import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CookieConsent from "react-cookie-consent";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { HowItWorks } from "./components/howitworks";
import { Features } from "./components/features";
import { Contact } from "./components/contact";
import { SEO, SEOProvider } from "./components/SEO";
import { getBrowserLanguage, saveLanguagePreference } from "./i18n";
import { trackCookieConsent } from "./utils/analytics";
import { usePageAnalytics } from "./hooks/usePageAnalytics";
import SmoothScroll from "smooth-scroll";
import "./App.css";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

const LandingPage = () => {
  const { t } = useTranslation();
  
  // Track page engagement metrics
  usePageAnalytics();
  
  return (
    <div>
      <SEO />
      <Navigation />
      <Header />
      <HowItWorks />
      <Features />
      <Contact />
      <CookieConsent
        location="bottom"
        buttonText={t('cookies.accept')}
        declineButtonText={t('cookies.decline')}
        enableDeclineButton
        cookieName="onbuild_cookie_consent"
        style={{ background: "#2B373B", alignItems: "center" }}
        buttonStyle={{ 
          background: "#5ca9fb", 
          color: "#fff", 
          fontSize: "14px",
          borderRadius: "4px",
          padding: "10px 20px"
        }}
        declineButtonStyle={{
          background: "transparent",
          color: "#fff",
          fontSize: "14px",
          border: "1px solid #fff",
          borderRadius: "4px",
          padding: "10px 20px"
        }}
        expires={365}
        onAccept={() => trackCookieConsent('accept')}
        onDecline={() => trackCookieConsent('decline')}
      >
        {t('cookies.message')}
      </CookieConsent>
    </div>
  );
};

const LanguageWrapper = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLanguageSet, setIsLanguageSet] = React.useState(false);

  useEffect(() => {
    const supportedLanguages = ['es', 'en', 'de', 'fr', 'ru', 'uk', 'ar', 'nl', 'it', 'ro', 'pl'];
    
    if (lang && supportedLanguages.includes(lang)) {
      // Save language preference
      saveLanguagePreference(lang);
      // Set language from URL
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang).then(() => {
          setIsLanguageSet(true);
        });
      } else {
        setIsLanguageSet(true);
      }
    } else if (lang) {
      // If language is not supported, redirect to browser language or Spanish
      const browserLang = getBrowserLanguage();
      navigate(`/${browserLang}`, { replace: true });
    }
  }, [lang, i18n, navigate]);

  // Don't render until language is set
  if (!isLanguageSet) {
    return null;
  }

  return <LandingPage />;
};

const RootRedirect = () => {
  const browserLang = getBrowserLanguage();
  return <Navigate to={`/${browserLang}`} replace />;
};

const App = () => {
  return (
    <SEOProvider>
      <Router>
      <Routes>
        {/* Root page - redirect to browser language */}
        <Route path="/" element={<RootRedirect />} />
        
        {/* Language routes */}
        <Route path="/:lang" element={<LanguageWrapper />} />
        
        {/* Fallback to Spanish */}
        <Route path="*" element={<Navigate to="/es" replace />} />
      </Routes>
    </Router>
    </SEOProvider>
  );
};

export default App;
